// src/screens/vuelosGuyana/VuelosGuyanaFlow.jsx
// Flujo compartido de navegación en dos niveles (destino → ciudad → rutas)
// reutilizado tanto por la pantalla logueada (VuelosGuyanaScreen) como por
// la versión pública sin login (VuelosGuyanaPublicScreen). La única
// diferencia entre ambas versiones es si se registra la cotización en
// vuelos_cotizaciones (requiere uid) -- todo lo demás es idéntico.
//
// Nivel 1: elige destino (Guyana / Suriname / México / Miami → Cuba).
// Nivel 2: Guyana/Suriname/México siguen el flujo de ciudad de salida ya
// existente, ahora filtrado por esa categoría puntual. Miami → Cuba es
// dirección inversa (ciudad_salida='Miami', destino=ciudad cubana) y no
// tiene selector de ciudad -- va directo a la lista de rutas.
import React, { useState, useEffect } from 'react';
import { toast } from '../../components/UI';
import { openWhatsApp, logVueloCotizacion } from '../../services/authService';
import {
  DESTINO_CATEGORIAS, CATEGORIAS_SIN_ZELLE, getDestinoCounts, getCiudadesSalidaPorDestino,
  getRutasByCiudadYDestino, getRutasPorCategoria, getTasaVuelos, getTasaEurVuelos, getTasaMxnVuelos,
  agruparFechasPorMes, formatMesLabel, formatFechaChip, CUPOS_URGENCIA,
  calcPrecioUsdCuba, calcPrecioUsdCubaFecha, calcPrecioReales, calcPrecioEur, calcPrecioMxn, logVuelosEvento, setVuelosTrackingOptOut,
  getRemitenteActivo, calcPrecioZelleMensaje, construirSaludoWhatsapp, sincronizarGestorActivo,
  calcPrecioZelleFecha, calcPrecioNinoFecha, normalizeKey,
} from '../../services/vuelosGuyanaService';

// Todos los precios ya vienen redondeados a entero desde los calc* del
// servicio -- estos formatters solo le dan formato, sin volver a meter
// decimales ni comas.
const formatUsd = (n) => `${Math.round(n)}`;
const formatBrl = (n) => Math.round(n).toLocaleString('es', { maximumFractionDigits: 0 });
const formatFechaLegible = (fechaStr) =>
  new Date(fechaStr + 'T00:00:00').toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' });

function Stepper({ label, value, onChange, min = 0, max = 9 }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
      <span style={{ fontSize: 13, color: 'var(--off-white)', fontWeight: 600 }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={() => onChange(Math.max(min, value - 1))} style={{
          width: 32, height: 32, borderRadius: 8, background: 'var(--navy-card)',
          border: '1px solid var(--navy-border)', color: 'var(--white)', fontSize: 16, fontWeight: 700,
        }}>−</button>
        <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--white)', minWidth: 20, textAlign: 'center' }}>{value}</span>
        <button onClick={() => onChange(Math.min(max, value + 1))} style={{
          width: 32, height: 32, borderRadius: 8, background: 'var(--orange)',
          border: 'none', color: 'white', fontSize: 16, fontWeight: 700,
        }}>+</button>
      </div>
    </div>
  );
}

function ReservaModal({ ruta, fecha, uid, referredBy, origen, onClose }) {
  const [adultos, setAdultos] = useState(1);
  const [infantes, setInfantes] = useState(0);
  const [ninos, setNinos] = useState(0);
  // Guyana y Suriname no tienen vuelos de solo ida disponibles -- ahí no se
  // deja elegir, siempre va ida y vuelta.
  // precio_zelle es un único precio por ruta -- no hay campo para guardar un
  // precio de solo ida distinto. República Dominicana y San Cristóbal, igual
  // que Guyana/Suriname, se venden siempre ida y vuelta en la app; quien
  // necesita solo ida lo cotiza Yoedis a mano por WhatsApp (decisión de
  // Yoedis, no un límite técnico que valga la pena resolver con un segundo
  // precio por ahora).
  // Excepción: dentro de 'guyana' el tramo de vuelta Guyana->Habana (antes en
  // Copa) es un vuelo suelto, no un paquete -- ahí es al revés, siempre solo
  // ida, sin toggle.
  const esVueltaGuyanaHabana = ruta.destino_categoria === 'guyana' && ruta.ciudad_salida === 'Guyana';
  const soloIdaYVuelta = ['guyana', 'suriname', 'republica_dominicana', 'san_cristobal'].includes(ruta.destino_categoria)
    && !esVueltaGuyanaHabana;
  const [idaYVuelta, setIdaYVuelta] = useState(esVueltaGuyanaHabana ? false : soloIdaYVuelta);
  const conNinos = ruta.categorias === 'adulto_nino_infante';

  // Con canal (Luis Omar) o gestor activo, el precio del mensaje cambia --
  // canal: costo + margen fijo, siempre igual sin importar la fecha. Gestor:
  // se resta su descuento del precio que esté activo. Esto NO toca ningún
  // precio mostrado en pantalla, solo el texto que se envía por WhatsApp.
  const remitente = getRemitenteActivo(origen);
  const fechaObj = ruta.fechas.find((f) => f.fecha === fecha);
  const precioZelleNormalOPromo = calcPrecioZelleFecha(ruta, fechaObj);
  const precioNino = fechaObj ? calcPrecioNinoFecha(ruta, fechaObj) : null;
  const precioZelle = calcPrecioZelleMensaje(precioZelleNormalOPromo, ruta.precio_base_usd, origen);
  const precioUsdCuba = calcPrecioUsdCubaFecha(fechaObj, precioZelle, ruta.porcentaje_usd_cuba, ruta.usd_cuba_redondeo_arriba);
  const precioReales = calcPrecioReales(precioZelle, ruta.tasaBrl);
  const precioEur = calcPrecioEur(precioZelle, ruta.tasaEur);
  const precioMxn = calcPrecioMxn(precioZelle, ruta.tasaMxn);

  const handleSend = () => {
    const partes = [`${adultos} adulto${adultos === 1 ? '' : 's'}`, `${infantes} infante${infantes === 1 ? '' : 's'}`];
    if (conNinos) partes.push(`${ninos} niño${ninos === 1 ? '' : 's'}`);

    const usdCubaTexto = ruta.admite_usd_cuba ? ` | USD Cuba: ${formatUsd(precioUsdCuba)} USD` : '';
    // Ruta "solo USD en Cuba": un único precio en USD (efectivo), sin las demás monedas.
    const preciosTexto = ruta.solo_usd_cuba
      ? `USD en Cuba: ${formatUsd(precioUsdCuba)} USD`
      : `Zelle: ${formatUsd(precioZelle)} USD${usdCubaTexto} | Pix: R$ ${formatBrl(precioReales)} | Euro: €${formatBrl(precioEur)} | Pesos: $${formatBrl(precioMxn)} MXN`;
    const mensaje = `${construirSaludoWhatsapp('Quiero cotizar este vuelo:', origen)}\n✈️ ${ruta.ciudad_salida} → ${ruta.destino}\n📅 ${formatFechaLegible(fecha)}\n💲 ${preciosTexto}\n👤 ${partes.join(', ')}\n🔁 ${idaYVuelta ? 'Ida y vuelta' : 'Solo ida'}`;

    if (uid) {
      logVueloCotizacion({
        uid, referredBy: referredBy || null,
        origen: ruta.ciudad_salida, destino: ruta.destino, fechaSolicitada: fecha,
        adultos, infantes,
      }).catch((err) => console.error('No se pudo registrar la cotización de vuelo:', err));
    }

    logVuelosEvento('clic_whatsapp', {
      origen, destinoCategoria: ruta.destino_categoria, ciudadSalida: ruta.ciudad_salida,
      destino: ruta.destino, fecha, rutaId: ruta.id,
    });

    openWhatsApp(mensaje);
    onClose();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 500, display: 'flex', alignItems: 'flex-end', backdropFilter: 'blur(4px)' }}>
      <div style={{ background: 'var(--navy-light)', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 480, margin: '0 auto', padding: '24px 20px 36px', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <p style={{ fontSize: 11, color: 'var(--orange)', fontWeight: 700, letterSpacing: 1 }}>RESERVAR VUELO</p>
            <h3 style={{ fontSize: 18, fontWeight: 800, color: 'var(--white)', marginTop: 2 }}>{ruta.ciudad_salida} → {ruta.destino}</h3>
            <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>📅 {formatFechaLegible(fecha)}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', color: 'var(--muted)', fontSize: 22 }}>✕</button>
        </div>

        <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, padding: 16, marginBottom: 20 }}>
          <Stepper label="Adultos" value={adultos} onChange={setAdultos} min={1} />
          <Stepper
            label={`Infantes (0-2 años)${ruta.precio_infante != null ? ` · $${formatUsd(parseFloat(ruta.precio_infante))}` : ''}`}
            value={infantes} onChange={setInfantes}
          />
          {conNinos && (
            <Stepper
              label={`Niños${precioNino != null ? ` · $${formatUsd(precioNino)}` : ''}`}
              value={ninos} onChange={setNinos}
            />
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 24 }}>
          {(esVueltaGuyanaHabana
            ? [{ v: false, label: 'Solo ida' }]
            : soloIdaYVuelta
              ? [{ v: true, label: 'Ida y vuelta' }]
              : [{ v: false, label: 'Solo ida' }, { v: true, label: 'Ida y vuelta' }]
          ).map((op) => (
            <button key={op.label} onClick={() => setIdaYVuelta(op.v)} disabled={soloIdaYVuelta || esVueltaGuyanaHabana} style={{
              flex: 1, padding: '12px 0', borderRadius: 10, fontSize: 13, fontWeight: 700,
              background: idaYVuelta === op.v ? 'rgba(255,107,0,0.20)' : 'var(--navy-card)',
              color: idaYVuelta === op.v ? 'var(--orange)' : 'var(--muted)',
              border: `1.5px solid ${idaYVuelta === op.v ? 'var(--orange)' : 'var(--navy-border)'}`,
            }}>{op.label}</button>
          ))}
        </div>

        <button className="btn-primary" onClick={handleSend}>💬 Enviar por WhatsApp</button>
      </div>
    </div>
  );
}

// Colores del precio por fecha: relativo al resto de fechas de la misma ruta.
const COLOR_TIER_PRECIO = { barato: 'var(--success)', medio: '#ffb020', caro: '#ff5a6a' };

function RutaCard({ ruta, tasaBrl, tasaEur, tasaMxn, uid, referredBy, origen }) {
  const grupos = agruparFechasPorMes(ruta.fechas);
  // Sin fecha preseleccionada -- si arrancara marcada la primera fecha,
  // alguien que solo mira la tarjeta puede pensar que ese precio aplica a
  // todos los días, cuando en rutas escalonadas cambia según cuál elija.
  // Así el precio no aparece hasta que el usuario toca una fecha él mismo.
  const [fechaSeleccionada, setFechaSeleccionada] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const hayFechas = ruta.fechas.length > 0;

  // El tramo de vuelta Guyana->Habana vive en la categoría 'guyana' pero es
  // un vuelo de Copa -- igual que el resto de Copa, Yoedis no cobra por
  // Zelle ahí, solo transferencia bancaria USD. Sin esta excepción se le
  // pega la etiqueta "ZELLE" de las demás rutas de Guyana por error.
  const esVueltaGuyanaHabana = ruta.destino_categoria === 'guyana' && ruta.ciudad_salida === 'Guyana';
  // Copa es la única con 2 tarifas por ruta según equipaje -- el resto del
  // catálogo nunca tiene precio_zelle_facturado, así que ahí este bloque no
  // hace nada (deltaFacturado queda null y el toggle no se muestra).
  const sinZelle = CATEGORIAS_SIN_ZELLE.includes(ruta.destino_categoria) || esVueltaGuyanaHabana;
  const zelleLabel = sinZelle ? 'TRANSFERENCIA USD' : 'ZELLE';
  const deltaFacturado = ruta.precio_zelle_facturado != null
    ? Math.round(parseFloat(ruta.precio_zelle_facturado) - parseFloat(ruta.precio_zelle))
    : null;
  // Rutas sin fechas (ej. México) que además traen precio de ida y vuelta: el cliente elige cuál ver.
  // precio_zelle es el de solo ida; precio_zelle_ida_vuelta el de ida y vuelta.
  const tieneIdaVuelta = !hayFechas && ruta.precio_zelle_ida_vuelta != null;
  const [idaYVueltaCard, setIdaYVueltaCard] = useState(false);
  const usaIdaVuelta = tieneIdaVuelta && idaYVueltaCard;
  const [equipaje, setEquipaje] = useState('mano');
  const extraEquipaje = (deltaFacturado != null && equipaje === 'facturado') ? deltaFacturado : 0;

  // Copa siempre trae 3 rutas juntas en la misma pantalla (a diferencia de
  // Guyana con 8) y cada una es más alta (toggle de equipaje + 4/5 monedas),
  // así que arranca colapsada -- solo un "desde $X" -- para que el footer de
  // WhatsApp/Descargar App siga visible sin mucho scroll. El resto de
  // categorías sigue mostrando todo abierto, como siempre.
  const esCopa = ruta.destino_categoria === 'copa';
  const [abierta, setAbierta] = useState(false);
  const estaAbierta = !esCopa || abierta;
  const precioDesde = Math.round(parseFloat(ruta.precio_zelle));

  const handleSeleccionarFecha = (fecha) => {
    setFechaSeleccionada(fecha);
    logVuelosEvento('ver_fecha', {
      origen, destinoCategoria: ruta.destino_categoria, ciudadSalida: ruta.ciudad_salida,
      destino: ruta.destino, fecha, rutaId: ruta.id, aerolinea: ruta.aerolinea,
    });
  };

  const fechaObjSeleccionada = ruta.fechas.find((f) => f.fecha === fechaSeleccionada);

  // Precio de cada fecha (mismo cálculo que el recuadro grande, incluye equipaje extra) y su color
  // relativo dentro de la ruta: verde = el más barato, rojo = el más caro, naranja = intermedio.
  // Si todas valen igual no se pinta nada (si no, parecería una oferta que no existe).
  const preciosPorFecha = {};
  ruta.fechas.forEach((f) => { preciosPorFecha[f.id] = calcPrecioZelleFecha(ruta, f) + extraEquipaje; });
  // En una ruta "solo USD en Cuba" cada fecha muestra el precio en efectivo (Zelle de esa fecha + el % de USD Cuba).
  // El color (más barato / intermedio / más caro) sigue saliendo del precio Zelle, que ordena igual.
  const preciosMostrados = {};
  ruta.fechas.forEach((f) => {
    preciosMostrados[f.id] = ruta.solo_usd_cuba
      ? calcPrecioUsdCubaFecha(f, preciosPorFecha[f.id], ruta.porcentaje_usd_cuba, ruta.usd_cuba_redondeo_arriba)
      : preciosPorFecha[f.id];
  });
  const listaPrecios = Object.values(preciosPorFecha);
  const precioMin = listaPrecios.length ? Math.min(...listaPrecios) : 0;
  const precioMax = listaPrecios.length ? Math.max(...listaPrecios) : 0;
  const tierPorFecha = {};
  ruta.fechas.forEach((f) => {
    const pr = preciosPorFecha[f.id];
    tierPorFecha[f.id] = precioMin === precioMax ? null : pr === precioMin ? 'barato' : pr === precioMax ? 'caro' : 'medio';
  });
  const hayColores = precioMin !== precioMax;
  const precioZelle = fechaObjSeleccionada ? calcPrecioZelleFecha(ruta, fechaObjSeleccionada) + extraEquipaje : null;
  const precioUsdCuba = precioZelle != null ? calcPrecioUsdCubaFecha(fechaObjSeleccionada, precioZelle, ruta.porcentaje_usd_cuba, ruta.usd_cuba_redondeo_arriba) : null;
  const precioReales = precioZelle != null ? calcPrecioReales(precioZelle, tasaBrl) : null;
  const precioEur = precioZelle != null ? calcPrecioEur(precioZelle, tasaEur) : null;
  const precioMxn = precioZelle != null ? calcPrecioMxn(precioZelle, tasaMxn) : null;
  const precioNinoSeleccionada = fechaObjSeleccionada ? calcPrecioNinoFecha(ruta, fechaObjSeleccionada) : null;

  // Cuando la ruta no tiene ninguna fecha cargada no hay nada que elegir --
  // ahí no aplica la ambigüedad "¿este precio es de qué día?", así que el
  // precio base se puede mostrar directo, sin esperar ningún toque.
  const precioBaseRuta = Math.round(parseFloat(usaIdaVuelta ? ruta.precio_zelle_ida_vuelta : ruta.precio_zelle)) + extraEquipaje;
  const precioUsdCubaBase = calcPrecioUsdCuba(precioBaseRuta, ruta.porcentaje_usd_cuba, ruta.usd_cuba_redondeo_arriba);
  const precioRealesBase = calcPrecioReales(precioBaseRuta, tasaBrl);
  const precioEurBase = calcPrecioEur(precioBaseRuta, tasaEur);
  const precioMxnBase = calcPrecioMxn(precioBaseRuta, tasaMxn);

  const handleWhatsAppGenerico = () => {
    // El canal calcula su propio precio (costo + margen fijo, ignora
    // precioBase); el gestor resta su descuento del precio ya mostrado.
    const remitente = getRemitenteActivo(origen);
    const zelleMsg = remitente ? calcPrecioZelleMensaje(precioBaseRuta, ruta.precio_base_usd, origen) : precioBaseRuta;
    const usdCubaMsg = remitente ? calcPrecioUsdCuba(zelleMsg, ruta.porcentaje_usd_cuba, ruta.usd_cuba_redondeo_arriba) : precioUsdCubaBase;
    const realesMsg = remitente ? calcPrecioReales(zelleMsg, tasaBrl) : precioRealesBase;
    const eurMsg = remitente ? calcPrecioEur(zelleMsg, tasaEur) : precioEurBase;
    const mxnMsg = remitente ? calcPrecioMxn(zelleMsg, tasaMxn) : precioMxnBase;
    const usdCubaTexto = ruta.admite_usd_cuba ? ` | USD Cuba: ${formatUsd(usdCubaMsg)} USD` : '';
    const equipajeTexto = deltaFacturado != null ? ` (${equipaje === 'facturado' ? 'con 23kg facturado' : 'solo equipaje de mano'})` : '';
    const viajeTexto = tieneIdaVuelta ? `\n🔁 ${usaIdaVuelta ? 'Ida y vuelta' : 'Solo ida'}` : '';
    const mensaje = `${construirSaludoWhatsapp('Quiero consultar disponibilidad actualizada para este vuelo:', origen)}\n✈️ ${ruta.ciudad_salida} → ${ruta.destino}${equipajeTexto}${viajeTexto}\n💲 ${ruta.solo_usd_cuba ? `USD en Cuba: ${formatUsd(usdCubaMsg)} USD` : `${sinZelle ? 'Transferencia' : 'Zelle'}: ${formatUsd(zelleMsg)} USD${usdCubaTexto} | Pix: R$ ${formatBrl(realesMsg)} | Euro: €${formatBrl(eurMsg)} | Pesos: $${formatBrl(mxnMsg)} MXN`}`;
    logVuelosEvento('clic_whatsapp', {
      origen, destinoCategoria: ruta.destino_categoria, ciudadSalida: ruta.ciudad_salida,
      destino: ruta.destino, rutaId: ruta.id,
    });
    openWhatsApp(mensaje);
  };

  return (
    <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 16, padding: 18, marginBottom: 14 }}>
      <div
        style={{ marginBottom: esCopa ? 0 : 12, cursor: esCopa ? 'pointer' : 'default' }}
        onClick={esCopa ? () => setAbierta((v) => !v) : undefined}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
          <div>
            <p style={{ fontSize: 15, fontWeight: 800, color: 'var(--white)' }}>{ruta.ciudad_salida} → {ruta.destino}</p>
            <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
              {[ruta.aerolinea, ruta.escala || 'Vuelo directo', ruta.frecuencia].filter(Boolean).join(' · ')}
            </p>
          </div>
          {esCopa && <span style={{ color: 'var(--muted)', fontSize: 13, transform: abierta ? 'rotate(90deg)' : 'none', flexShrink: 0 }}>▸</span>}
        </div>
        {esCopa && !abierta && (
          <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>Desde ${formatUsd(precioDesde)} USD · toca para ver fechas</p>
        )}
        {/* Varias tarjetas de este mismo tramo conviven con precios distintos
            según la fecha (ej. Guyana->Habana, repartida en varios tramos de
            precio) -- sin esto todas dicen "Guyana → Habana" igual y el
            cliente no puede distinguirlas antes de entrar a buscar fecha. */}
        {esVueltaGuyanaHabana && (
          <p style={{ fontSize: 13, color: 'var(--gold)', fontWeight: 800, marginTop: 6 }}>Desde ${formatUsd(precioDesde)} USD</p>
        )}
      </div>

      {estaAbierta && hayFechas && !fechaObjSeleccionada && (
        <div style={{ border: '1.5px dashed var(--navy-border)', borderRadius: 10, padding: 14, textAlign: 'center', marginBottom: 16 }}>
          <p style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 700 }}>👇 Toca una fecha para ver el precio en USD en efectivo, Reales, Euro y más</p>
        </div>
      )}

      {estaAbierta && (!hayFechas || fechaObjSeleccionada) && (
        <>
          {tieneIdaVuelta && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              {[[false, 'Solo ida'], [true, 'Ida y vuelta']].map(([v, label]) => (
                <button key={label} onClick={() => setIdaYVueltaCard(v)} style={{
                  flex: 1, padding: '9px 6px', borderRadius: 10, fontSize: 12, fontWeight: 700,
                  background: idaYVueltaCard === v ? 'rgba(255,107,0,0.16)' : 'var(--navy)',
                  color: idaYVueltaCard === v ? 'var(--orange)' : 'var(--muted)',
                  border: `1.5px solid ${idaYVueltaCard === v ? 'var(--orange)' : 'var(--navy-border)'}`,
                }}>{label}</button>
              ))}
            </div>
          )}
          {deltaFacturado != null && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              {[['mano', 'Equipaje de mano'], ['facturado', `+23kg (+$${formatUsd(deltaFacturado)})`]].map(([id, label]) => (
                <button key={id} onClick={() => setEquipaje(id)} style={{
                  flex: 1, padding: '9px 6px', borderRadius: 10, fontSize: 11.5, fontWeight: 700,
                  background: equipaje === id ? 'rgba(255,107,0,0.16)' : 'var(--navy)',
                  color: equipaje === id ? 'var(--orange)' : 'var(--muted)',
                  border: `1.5px solid ${equipaje === id ? 'var(--orange)' : 'var(--navy-border)'}`,
                }}>{label}</button>
              ))}
            </div>
          )}
          {ruta.solo_usd_cuba ? (
            // Ruta "solo USD en Cuba": un único recuadro con el precio en efectivo.
            <div style={{ marginBottom: 16 }}>
              <div style={{ background: 'rgba(255,184,0,0.08)', border: '1px solid rgba(255,184,0,0.25)', borderRadius: 10, padding: '10px 6px', textAlign: 'center' }}>
                <p style={{ fontSize: 9, color: 'var(--muted)' }}>USD EN CUBA (EFECTIVO)</p>
                <p style={{ fontSize: 16, fontWeight: 800, color: 'var(--gold)' }}>${formatUsd(hayFechas ? precioUsdCuba : precioUsdCubaBase)}</p>
              </div>
            </div>
          ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
            <div style={{ background: 'rgba(0,196,140,0.08)', border: '1px solid rgba(0,196,140,0.25)', borderRadius: 10, padding: '8px 6px', textAlign: 'center' }}>
              <p style={{ fontSize: 9, color: 'var(--muted)' }}>{zelleLabel}</p>
              <p style={{ fontSize: 14, fontWeight: 800, color: 'var(--success)' }}>${formatUsd(hayFechas ? precioZelle : precioBaseRuta)}</p>
            </div>
            {ruta.admite_usd_cuba && (
              <div style={{ background: 'rgba(255,184,0,0.08)', border: '1px solid rgba(255,184,0,0.25)', borderRadius: 10, padding: '8px 6px', textAlign: 'center' }}>
                <p style={{ fontSize: 9, color: 'var(--muted)' }}>USD CUBA</p>
                <p style={{ fontSize: 14, fontWeight: 800, color: 'var(--gold)' }}>${formatUsd(hayFechas ? precioUsdCuba : precioUsdCubaBase)}</p>
              </div>
            )}
            <div style={{ background: 'rgba(255,107,0,0.08)', border: '1px solid rgba(255,107,0,0.25)', borderRadius: 10, padding: '8px 6px', textAlign: 'center' }}>
              <p style={{ fontSize: 9, color: 'var(--muted)' }}>REALES</p>
              <p style={{ fontSize: 14, fontWeight: 800, color: 'var(--orange)' }}>R${formatBrl(hayFechas ? precioReales : precioRealesBase)}</p>
            </div>
            <div style={{ background: 'rgba(120,140,255,0.08)', border: '1px solid rgba(120,140,255,0.25)', borderRadius: 10, padding: '8px 6px', textAlign: 'center' }}>
              <p style={{ fontSize: 9, color: 'var(--muted)' }}>EURO</p>
              <p style={{ fontSize: 14, fontWeight: 800, color: '#9aa8ff' }}>€{formatBrl(hayFechas ? precioEur : precioEurBase)}</p>
            </div>
            {/* Azul de marca (logo Click Travel Agency) -- más saturado que el
                lavanda del Euro para que no se confundan entre sí. Ocupa el
                ancho completo cuando hay USD Cuba (5 tarjetas), y encaja
                directo en la grilla 2x2 cuando no lo hay (4 tarjetas). */}
            <div style={{
              background: 'rgba(30,95,191,0.08)', border: '1px solid rgba(30,95,191,0.3)', borderRadius: 10, padding: '8px 6px', textAlign: 'center',
              gridColumn: ruta.admite_usd_cuba ? '1 / -1' : undefined,
            }}>
              <p style={{ fontSize: 9, color: 'var(--muted)' }}>PESOS MXN</p>
              <p style={{ fontSize: 14, fontWeight: 800, color: '#1E5FBF' }}>${formatBrl(hayFechas ? precioMxn : precioMxnBase)}</p>
            </div>
          </div>
          )}

          {(ruta.precio_infante != null || (ruta.categorias === 'adulto_nino_infante' && precioNinoSeleccionada != null)) && (
            <p style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 14 }}>
              {ruta.precio_infante != null && `👶 Infante: $${formatUsd(parseFloat(ruta.precio_infante))}`}
              {ruta.categorias === 'adulto_nino_infante' && precioNinoSeleccionada != null &&
                `${ruta.precio_infante != null ? '   ·   ' : ''}🧒 Niño: $${formatUsd(precioNinoSeleccionada)}`}
            </p>
          )}
        </>
      )}

      {estaAbierta && (hayFechas ? (
        <>
          {grupos.map(([mesKey, fechas]) => (
            <div key={mesKey} style={{ marginBottom: 10 }}>
              <p style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 700, letterSpacing: 0.5, marginBottom: 6 }}>{formatMesLabel(mesKey)}</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {fechas.map((f) => {
                  const urgente = f.cupos != null && f.cupos <= CUPOS_URGENCIA;
                  const selected = fechaSeleccionada === f.fecha;
                  return (
                    <button key={f.id} onClick={() => handleSeleccionarFecha(f.fecha)} style={{
                      padding: '6px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700, textAlign: 'center',
                      background: selected ? 'rgba(255,107,0,0.22)' : 'var(--navy-card)',
                      color: selected ? 'var(--orange)' : 'var(--off-white)',
                      border: `1.5px solid ${urgente ? 'var(--error)' : (selected ? 'var(--orange)' : 'var(--navy-border)')}`,
                    }}>
                      {formatFechaChip(f.fecha)}
                      <span style={{ display: 'block', fontSize: 12, fontWeight: 800, marginTop: 2, color: COLOR_TIER_PRECIO[tierPorFecha[f.id]] || 'var(--muted)' }}>
                        ${formatUsd(preciosMostrados[f.id])}
                      </span>
                      {f.cupos != null && <span style={{ display: 'block', fontSize: 9.5, fontWeight: 600, opacity: 0.75 }}>{f.cupos} lugares</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {hayColores && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 10.5, color: 'var(--muted)', marginTop: 2 }}>
              {[['barato', 'Más barato'], ['medio', 'Intermedio'], ['caro', 'Más caro']].map(([k, label]) => (
                <span key={k}><span style={{ color: COLOR_TIER_PRECIO[k] }}>●</span> {label}</span>
              ))}
            </div>
          )}
          {fechaSeleccionada ? (
            <button className="btn-primary" style={{ marginTop: 8 }} onClick={() => setShowModal(true)}>Reservar esta →</button>
          ) : (
            <button disabled style={{
              marginTop: 8, width: '100%', background: 'var(--navy-card)', border: '1px solid var(--navy-border)',
              color: 'var(--muted)', borderRadius: 10, padding: 12, fontSize: 14, fontWeight: 700, cursor: 'default',
            }}>Elige una fecha para reservar</button>
          )}
        </>
      ) : (
        <>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12, lineHeight: 1.5 }}>
            Consulta disponibilidad actualizada por WhatsApp.
          </p>
          <button className="btn-primary" onClick={handleWhatsAppGenerico}>💬 Consultar por WhatsApp</button>
        </>
      ))}

      {showModal && fechaSeleccionada && (
        <ReservaModal
          ruta={{ ...ruta, tasaBrl, tasaEur, tasaMxn }}
          fecha={fechaSeleccionada}
          uid={uid}
          referredBy={referredBy}
          origen={origen}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

function EstadoVacio({ label, origen, destinoCategoria, ciudadSalida }) {
  // Demanda no cubierta: se dispara una sola vez cuando este estado vacío
  // aparece (rutas/ciudad buscadas y no hay nada activo) -- da la señal más
  // directa de qué inventario conseguir a continuación.
  useEffect(() => {
    logVuelosEvento('sin_resultados', { origen, destinoCategoria, ciudadSalida });
  }, [origen, destinoCategoria, ciudadSalida]);

  const handleConsultar = () => {
    logVuelosEvento('clic_whatsapp', { origen, destinoCategoria, ciudadSalida });
    openWhatsApp(construirSaludoWhatsapp(`Quiero consultar disponibilidad de vuelos hacia ${label}.`, origen));
  };
  return (
    <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 16, padding: 24, textAlign: 'center' }}>
      <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16, lineHeight: 1.5 }}>
        Todavía no hay rutas publicadas hacia {label}. Consulta disponibilidad actualizada por WhatsApp.
      </p>
      <button className="btn-primary" onClick={handleConsultar}>💬 Consultar por WhatsApp</button>
    </div>
  );
}

export default function VuelosGuyanaFlow({ uid, referredBy, onBack, origen, onNivelChange }) {
  const [categorias, setCategorias] = useState([]);
  const [categoria, setCategoria] = useState(null);
  const [ciudades, setCiudades] = useState([]);
  const [ciudad, setCiudad] = useState(null);
  const [rutas, setRutas] = useState([]);
  const [tasaBrl, setTasaBrl] = useState(5.2);
  const [tasaEur, setTasaEur] = useState(0.86);
  const [tasaMxn, setTasaMxn] = useState(17.5);
  const [loading, setLoading] = useState(true);
  const [loadingNivel2, setLoadingNivel2] = useState(false);

  // Le avisa a la pantalla que lo envuelve (VuelosGuyanaPublicScreen) si
  // seguimos en el selector de destino o si ya se entró al embudo -- ahí el
  // footer de redes/grupo/app se oculta para no distraer.
  useEffect(() => {
    onNivelChange?.(categoria == null ? 'inicio' : 'interno');
  }, [categoria, onNivelChange]);

  useEffect(() => {
    Promise.all([getDestinoCounts(), getTasaVuelos(), getTasaEurVuelos(), getTasaMxnVuelos()]).then(([c, t, te, tm]) => {
      setCategorias(c);
      setTasaBrl(t);
      setTasaEur(te);
      setTasaMxn(tm);
      setLoading(false);

      // Enlace directo a destino (+ ciudad, opcional) -- ej. ?destino=guyana
      // o ?destino=guyana&ciudad=Habana -- generado desde el botón "Copiar
      // enlace" de la app logueada (nunca visible al cliente) para mandarle
      // a un cliente puntual directo a esa ciudad, sin que tenga que elegir
      // destino y ciudad él mismo. Igual puede tocar "atrás" y seguir
      // navegando normal desde ahí.
      const params = new URLSearchParams(window.location.search);
      const destinoParam = params.get('destino');
      const ciudadParam = params.get('ciudad');
      const cat = destinoParam ? c.find((x) => x.id === destinoParam) : null;
      if (cat) {
        setCategoria(cat);
        logVuelosEvento('ver_destino', { origen, destinoCategoria: cat.id });
        if (cat.id === 'miami_cuba') {
          setLoadingNivel2(true);
          getRutasPorCategoria(cat.id).then((r) => { setRutas(r); setLoadingNivel2(false); });
        } else {
          setLoadingNivel2(true);
          getCiudadesSalidaPorDestino(cat.id).then((lista) => {
            setCiudades(lista);
            setLoadingNivel2(false);
            if (ciudadParam) {
              setCiudad(ciudadParam);
              logVuelosEvento('ver_ciudad', { origen, destinoCategoria: cat.id, ciudadSalida: ciudadParam });
              setLoadingNivel2(true);
              getRutasByCiudadYDestino(ciudadParam, cat.id).then((r) => { setRutas(r); setLoadingNivel2(false); });
            }
          });
        }
      }
    });
    // ?notrack=1 en el enlace público -- para que Yoedis pueda abrirlo una
    // vez en cada teléfono/navegador propio y que esas visitas nunca se
    // cuenten en las estadísticas de ese dispositivo en adelante.
    if (new URLSearchParams(window.location.search).get('notrack') === '1') {
      setVuelosTrackingOptOut(true);
    }
    // Si el dispositivo llegó marcado con el link de un gestor, resuelve su
    // nombre/descuento contra la base de datos y lo cachea para que el
    // mensaje de WhatsApp pueda armarse de forma síncrona más adelante.
    sincronizarGestorActivo();
    logVuelosEvento('visita', { origen });
  }, []);

  const handleSelectCategoria = async (cat) => {
    setCategoria(cat);
    logVuelosEvento('ver_destino', { origen, destinoCategoria: cat.id });
    if (cat.id === 'miami_cuba') {
      setLoadingNivel2(true);
      setRutas(await getRutasPorCategoria(cat.id));
      setLoadingNivel2(false);
    } else {
      setLoadingNivel2(true);
      setCiudades(await getCiudadesSalidaPorDestino(cat.id));
      setLoadingNivel2(false);
    }
  };

  const handleSelectCiudad = async (c) => {
    setCiudad(c);
    logVuelosEvento('ver_ciudad', { origen, destinoCategoria: categoria.id, ciudadSalida: c });
    setLoadingNivel2(true);
    setRutas(await getRutasByCiudadYDestino(c, categoria.id));
    setLoadingNivel2(false);
  };

  const volverACategoria = () => { setCategoria(null); setCiudad(null); setCiudades([]); setRutas([]); };
  const volverACiudad = () => { setCiudad(null); setRutas([]); };

  // Solo para la app logueada (nunca aparece en el link público que se le
  // comparte al cliente): arma un enlace directo a esta ciudad+destino sobre
  // la ruta pública /vuelos-guyana, para que quien lo abra entre derecho a
  // ver esas fechas/precios sin elegir destino y ciudad él mismo -- igual
  // puede tocar "atrás" y seguir navegando normal desde ahí.
  const handleCopiarEnlace = (categoriaId, ciudadTexto) => {
    const params = new URLSearchParams({ destino: categoriaId, ...(ciudadTexto ? { ciudad: ciudadTexto } : {}) });
    const url = `${window.location.origin}/vuelos-guyana?${params.toString()}`;
    (navigator.clipboard?.writeText(url) ?? Promise.reject())
      .then(() => toast('Enlace copiado ✅'))
      .catch(() => toast('No se pudo copiar el enlace.', 'error'));
  };

  // ── Nivel 2, Miami → Cuba: lista directa de rutas, sin selector de ciudad ──
  if (categoria?.id === 'miami_cuba') {
    return (
      <div style={{ padding: '20px 16px 40px' }}>
        <button onClick={volverACategoria} style={{ background: 'none', color: 'var(--muted)', fontSize: 13, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>← Elegir otro destino</button>
        <div style={{ marginBottom: 18 }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--white)' }}>{categoria.icon} {categoria.label}</h2>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>Precios y fechas actualizados</p>
        </div>
        {loadingNivel2 && <p style={{ color: 'var(--muted)', textAlign: 'center', padding: 20 }}>Cargando rutas...</p>}
        {!loadingNivel2 && rutas.length === 0 && (
          <EstadoVacio label={categoria.label} origen={origen} destinoCategoria={categoria.id} />
        )}
        {!loadingNivel2 && rutas.map((r) => (
          <RutaCard key={r.id} ruta={r} tasaBrl={tasaBrl} tasaEur={tasaEur} tasaMxn={tasaMxn} uid={uid} referredBy={referredBy} origen={origen} />
        ))}
      </div>
    );
  }

  // ── Nivel 2, resto de destinos con ciudad elegida: lista de rutas ──
  if (categoria && ciudad) {
    // Cuando la ciudad de salida coincide con el nombre de la categoría (el
    // tramo de vuelta Guyana->Habana dentro de 'guyana'), "Guyana desde
    // Guyana" no dice nada -- se muestra "Guyana → Habana" en su lugar,
    // igual que el botón de ciudad.
    const esRegresoMismaCiudad = normalizeKey(ciudad) === normalizeKey(categoria.label);
    const tituloNivel2 = esRegresoMismaCiudad && rutas[0]
      ? `${ciudad} → ${rutas[0].destino}`
      : `${categoria.label} desde ${ciudad}`;
    return (
      <div style={{ padding: '20px 16px 40px' }}>
        <button onClick={volverACiudad} style={{ background: 'none', color: 'var(--muted)', fontSize: 13, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>← Cambiar ciudad</button>
        <div style={{ marginBottom: 18 }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--white)' }}>{categoria.icon} {tituloNivel2}</h2>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>Precios y fechas actualizados</p>
        </div>
        {loadingNivel2 && <p style={{ color: 'var(--muted)', textAlign: 'center', padding: 20 }}>Cargando rutas...</p>}
        {!loadingNivel2 && rutas.length === 0 && (
          <EstadoVacio label={`${categoria.label} desde ${ciudad}`} origen={origen} destinoCategoria={categoria.id} ciudadSalida={ciudad} />
        )}
        {!loadingNivel2 && rutas.map((r) => (
          <RutaCard key={r.id} ruta={r} tasaBrl={tasaBrl} tasaEur={tasaEur} tasaMxn={tasaMxn} uid={uid} referredBy={referredBy} origen={origen} />
        ))}
      </div>
    );
  }

  // ── Nivel 2, resto de destinos sin ciudad elegida: selector de ciudad ──
  if (categoria) {
    const handleWhatsAppDuda = () => {
      logVuelosEvento('clic_whatsapp', { origen, destinoCategoria: categoria.id });
      openWhatsApp(construirSaludoWhatsapp(`Tengo una duda sobre los vuelos a ${categoria.label}.`, origen));
    };
    return (
      <div style={{ padding: '20px 16px 40px' }}>
        <button onClick={volverACategoria} style={{ background: 'none', color: 'var(--muted)', fontSize: 13, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>← Elegir otro destino</button>
        <div style={{ marginBottom: 14 }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--white)' }}>{categoria.icon} {categoria.label}</h2>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>Elige tu ciudad de salida</p>
        </div>

        {/* Detalles importantes -- se leen ANTES de elegir ciudad, así la
            pantalla de fechas (que en ciudades como Habana tiene muchas)
            no queda recargada repitiendo esto por cada ruta. */}
        <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, padding: '14px 16px', marginBottom: 16 }}>
          <p style={{ margin: '0 0 10px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', color: 'var(--orange)' }}>DETALLES IMPORTANTES</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--off-white)', lineHeight: 1.4 }}>✈️ Salida desde Cuba: pasaje de ida y vuelta</p>
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--off-white)', lineHeight: 1.4 }}>🧳 Salida desde Cuba: 1 maleta de 23 kg incluida en todos los vuelos</p>
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--off-white)', lineHeight: 1.4 }}>💵 USD en efectivo: La Habana · Matanzas · Camagüey · Holguín · Granma · Santiago</p>
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--off-white)', lineHeight: 1.4 }}>✔️ Emisión inmediata · Boleto verificable</p>
            {categoria.id === 'guyana' && (
              <p style={{ margin: 0, fontSize: 12.5, color: 'var(--off-white)', lineHeight: 1.4 }}>🔁 Salida desde Guyana: vuelo solo ida, con su propia política de equipaje (se muestra en la tarjeta del vuelo)</p>
            )}
          </div>
        </div>

        {loadingNivel2 && <p style={{ color: 'var(--muted)', textAlign: 'center', padding: 20 }}>Cargando ciudades...</p>}
        {!loadingNivel2 && ciudades.length === 0 && (
          <EstadoVacio label={categoria.label} origen={origen} destinoCategoria={categoria.id} />
        )}
        {!loadingNivel2 && ciudades.length > 0 && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
              {ciudades.map((c) => (
                <div key={c.ciudad} style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => handleSelectCiudad(c.ciudad)} style={{
                    flex: 1, background: 'rgba(255,107,0,0.10)', border: '1.5px solid rgba(255,107,0,0.3)',
                    borderRadius: 14, padding: '18px 14px', textAlign: 'center', color: 'var(--white)',
                    fontSize: 14, fontWeight: 800,
                  }}>🛫 {c.label}</button>
                  {origen === 'autenticado' && (
                    <button
                      onClick={() => handleCopiarEnlace(categoria.id, c.ciudad)}
                      title={`Copiar enlace directo a ${c.label}`}
                      style={{ width: 40, borderRadius: 14, background: 'var(--navy-card)', border: '1.5px solid var(--navy-border)', color: 'var(--muted)', fontSize: 15 }}
                    >📋</button>
                  )}
                </div>
              ))}
            </div>

            {/* WhatsApp secundario -- chico, después de la acción principal
                (elegir ciudad) para no robarle atención. Para quien tiene una
                duda y no va a cotizar todavía, sobre todo si viene de redes
                sociales o del link de un gestor. */}
            <button onClick={handleWhatsAppDuda} style={{
              width: '100%', textAlign: 'left', background: 'rgba(0,196,140,0.08)', border: '1px solid rgba(0,196,140,0.25)',
              borderRadius: 12, padding: '10px 13px', display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <span style={{ fontSize: 15 }}>💬</span>
              <span>
                <span style={{ display: 'block', fontSize: 11.5, fontWeight: 700, color: 'var(--success)' }}>Atención por WhatsApp</span>
                <span style={{ display: 'block', fontSize: 10, color: 'var(--muted)' }}>Escríbenos directamente si tienes dudas.</span>
              </span>
            </button>
          </>
        )}
      </div>
    );
  }

  // ── Nivel 1: selector de destino ──
  return (
    <div style={{ padding: '20px 16px 40px' }}>
      {onBack && (
        <button onClick={onBack} style={{ background: 'none', color: 'var(--muted)', fontSize: 13, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>← Volver</button>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{ width: 36, height: 36, background: 'rgba(255,107,0,0.22)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>✈️</div>
        <div>
          <h2 style={{ fontSize: 17, fontWeight: 800, color: 'var(--white)' }}>Vuelos disponibles</h2>
          <p style={{ fontSize: 11, color: 'var(--muted)' }}>Consulta rutas y fechas disponibles</p>
        </div>
      </div>

      {loading && <p style={{ color: 'var(--muted)', textAlign: 'center', padding: 20 }}>Cargando destinos...</p>}
      {!loading && (() => {
        // Guyana es, con diferencia, el destino que más tráfico trae -- se
        // muestra como tarjeta destacada de ancho completo, y el resto en
        // una grilla de 3 columnas más chica. Con 7 categorías (desde que
        // se sumó Copa) la grilla de 2 columnas de antes ya no cabía sin
        // scroll junto con el footer de WhatsApp/redes -- este layout baja
        // bastante la altura total sin sacar ninguna categoría de la vista.
        // Un destino sin rutas activas simplemente no aparece (antes salía una
        // tarjeta gris "Sin rutas"). Los enlaces directos ?destino=... a un
        // destino vacío siguen funcionando: usan la lista completa de arriba.
        const destacada = categorias.find((c) => c.id === 'guyana' && c.count > 0);
        const resto = categorias.filter((c) => c.id !== 'guyana' && c.count > 0);
        if (!destacada && resto.length === 0) {
          return <p style={{ color: 'var(--muted)', textAlign: 'center', padding: 20, fontSize: 13 }}>Por ahora no hay rutas publicadas. Escríbenos por WhatsApp y te ayudamos.</p>;
        }
        return (
          <>
            <p className="section-label" style={{ marginBottom: 8 }}>ELIGE TU DESTINO</p>
            {destacada && (
              <div style={{ position: 'relative', marginBottom: 8 }}>
                <button onClick={() => handleSelectCategoria(destacada)} style={{
                  width: '100%', textAlign: 'center',
                  background: destacada.count > 0 ? 'rgba(255,107,0,0.10)' : 'var(--navy-card)',
                  border: `1.5px solid ${destacada.count > 0 ? 'rgba(255,107,0,0.3)' : 'var(--navy-border)'}`,
                  borderRadius: 14, padding: '11px 10px',
                }}>
                  <p style={{ fontSize: 22, marginBottom: 2 }}>{destacada.icon}</p>
                  <p style={{ fontSize: 14, fontWeight: 800, color: 'var(--white)' }}>{destacada.label}</p>
                  <p style={{ fontSize: 10.5, color: destacada.count > 0 ? 'var(--success)' : 'var(--muted)', marginTop: 1 }}>
                    {destacada.count > 0 ? `${destacada.count} ruta${destacada.count === 1 ? '' : 's'}` : 'Sin rutas'}
                  </p>
                </button>
                {origen === 'autenticado' && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleCopiarEnlace(destacada.id); }}
                    title={`Copiar enlace directo a ${destacada.label}`}
                    style={{ position: 'absolute', top: 6, right: 6, width: 28, height: 28, borderRadius: 8, background: 'var(--navy-card)', border: '1.5px solid var(--navy-border)', color: 'var(--muted)', fontSize: 13 }}
                  >📋</button>
                )}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
              {resto.map((cat) => (
                <div key={cat.id} style={{ position: 'relative' }}>
                  <button onClick={() => handleSelectCategoria(cat)} style={{
                    width: '100%', textAlign: 'center', background: cat.count > 0 ? 'rgba(255,107,0,0.10)' : 'var(--navy-card)',
                    border: `1.5px solid ${cat.count > 0 ? 'rgba(255,107,0,0.3)' : 'var(--navy-border)'}`,
                    borderRadius: 10, padding: '8px 4px',
                  }}>
                    <p style={{ fontSize: 16, marginBottom: 2 }}>{cat.icon}</p>
                    <p style={{ fontSize: 9.5, fontWeight: 800, color: 'var(--white)', lineHeight: 1.2 }}>{cat.label}</p>
                    <p style={{ fontSize: 8, color: cat.count > 0 ? 'var(--success)' : 'var(--muted)', marginTop: 1 }}>
                      {cat.count > 0 ? `${cat.count} ruta${cat.count === 1 ? '' : 's'}` : 'Sin rutas'}
                    </p>
                  </button>
                  {origen === 'autenticado' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleCopiarEnlace(cat.id); }}
                      title={`Copiar enlace directo a ${cat.label}`}
                      style={{ position: 'absolute', top: 2, right: 2, width: 20, height: 20, borderRadius: 6, background: 'var(--navy-card)', border: '1px solid var(--navy-border)', color: 'var(--muted)', fontSize: 10, padding: 0 }}
                    >📋</button>
                  )}
                </div>
              ))}
            </div>
          </>
        );
      })()}
    </div>
  );
}
